import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { REPORT_TYPES, getReportTypesByCategory } from '../types/reportTypes';
import { getReportData } from '../data/sampleReportData';
import InteractiveReport from '../components/reports/InteractiveReport';

const ReportGalleryPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedReportType, setSelectedReportType] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: 'All Reports', count: REPORT_TYPES.length },
    { id: 'respiratory', name: 'Respiratory', count: getReportTypesByCategory('respiratory').length },
    { id: 'gastrointestinal', name: 'Gastrointestinal', count: getReportTypesByCategory('gastrointestinal').length },
    { id: 'urinary', name: 'Urinary', count: getReportTypesByCategory('urinary').length },
    { id: 'wound', name: 'Wound', count: getReportTypesByCategory('wound').length }
  ];

  const filteredReports = selectedCategory === 'all' 
    ? REPORT_TYPES 
    : getReportTypesByCategory(selectedCategory);

  const categoryColors = {
    respiratory: 'bg-blue-50 border-blue-200 text-blue-800',
    gastrointestinal: 'bg-green-50 border-green-200 text-green-800',
    urinary: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    wound: 'bg-red-50 border-red-200 text-red-800'
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/90 to-secondary/90 text-text-light py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary">
              PCR Panel Report Gallery
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-90 text-primary">
              Comprehensive molecular diagnostic reports for various specimen types and clinical scenarios.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-primary mb-4 text-center">Browse by Category</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'border-primary bg-primary text-white'
                    : 'border-border bg-background-alt text-text hover:border-primary/50'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>

        {/* Report Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredReports.map((reportType) => {
            const reportData = getReportData(reportType.id);
            const detectedPathogens = reportData?.analysis_results?.pathogens?.detected;
            const pathogenCount = [
              ...(detectedPathogens?.bacteria || []),
              ...(detectedPathogens?.viruses || []),
              ...(detectedPathogens?.fungi || []),
              ...(detectedPathogens?.parasites || [])
            ].filter(p => p.result === 'Detected').length;

            return (
              <motion.div
                key={reportType.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-background-alt rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${categoryColors[reportType.category]}`}>
                      {reportType.category.charAt(0).toUpperCase() + reportType.category.slice(1)}
                    </div>
                    <span className="text-sm text-secondary font-mono bg-background px-2 py-1 rounded">
                      {reportType.panelSize} tests
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-primary mb-2">
                    {reportType.name}
                  </h3>
                  
                  <p className="text-sm text-text mb-4">
                    {reportType.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-secondary mb-4">
                    <span>{reportType.specimenType}</span>
                    <span>{pathogenCount} pathogen{pathogenCount !== 1 ? 's' : ''} detected</span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedReportType(reportType.id)}
                      className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors text-sm"
                    >
                      View Interactive Report
                    </button>
                    <button
                      onClick={() => window.open(reportType.pdfPath, '_blank')}
                      className="flex-1 bg-secondary text-white px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors text-sm"
                    >
                      View PDF
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Interactive Report Modal/Section */}
        {selectedReportType && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedReportType(null)}
          >
            <div 
              className="bg-background rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-2xl font-bold text-primary">
                  {REPORT_TYPES.find(r => r.id === selectedReportType)?.name}
                </h2>
                <button
                  onClick={() => setSelectedReportType(null)}
                  className="text-secondary hover:text-primary text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <InteractiveReport 
                  initialReportType={selectedReportType} 
                  initialTab="summary"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Statistics Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-primary/5 rounded-xl p-8 border border-primary/20 text-center"
        >
          <h3 className="text-2xl font-semibold text-primary mb-4">Comprehensive PCR Testing Portfolio</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="text-3xl font-bold text-primary">{REPORT_TYPES.length}</div>
              <div className="text-sm text-secondary">Panel Types</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">4</div>
              <div className="text-sm text-secondary">Clinical Categories</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">56-112</div>
              <div className="text-sm text-secondary">Tests per Panel</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">100%</div>
              <div className="text-sm text-secondary">Molecular Accuracy</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportGalleryPage; 